import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Clock,
  Building2,
  ChevronRight,
  CalendarDays,
  Sparkles,
  IndianRupee,
  Search,
  X,
  ChevronDown,
} from "lucide-react";
import Navbar from "./landing/Navbar";
import Footer from "./landing/Footer";
import { walkinDriveService } from "../services/walkinDriveService";
import { WALKIN_DRIVES } from "../data/walkinDrives";
import { encodeSlug } from "../utils/slug";

/* ------------------------------ small bits ------------------------------ */

const MetaChip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-ink-500">
    <Icon className="w-4 h-4 text-brand-purple shrink-0" />
    <span className="truncate">{children}</span>
  </span>
);

/* ------------------------------- list card ------------------------------ */

export function DriveCard({ drive }) {
  return (
    <Link
      to={`/walkin-drives/${encodeSlug(drive.id)}`}
      className="group block rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-brand-purple/30 transition-all overflow-hidden"
    >
      <div className="p-5 sm:p-6 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-saffron/10 text-brand-saffron text-[10px] font-black uppercase tracking-wide">
            <Sparkles className="w-3 h-3" />
            Walk-in Drive
          </span>

          <h3 className="mt-3 font-display text-lg sm:text-xl font-black text-ink-900 leading-snug group-hover:text-brand-purple transition-colors">
            {drive.title}
          </h3>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <MetaChip icon={Building2}>{drive.company}</MetaChip>
            <MetaChip icon={MapPin}>{drive.venue}</MetaChip>
            {drive.meta?.experience && (
              <MetaChip icon={Clock}>{drive.meta.experience}</MetaChip>
            )}
            {drive.salary && (
              <MetaChip icon={IndianRupee}>{drive.salary}</MetaChip>
            )}
          </div>

          {drive.timeAndVenue?.dates && (
            <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-ink-500">
              <CalendarDays className="w-4 h-4 text-brand-purple shrink-0" />
              <span>
                {drive.timeAndVenue.dates}
                {drive.timeAndVenue.time && ` · ${drive.timeAndVenue.time}`}
              </span>
            </div>
          )}

          <span className="mt-4 flex w-fit items-center gap-1 text-sm font-black text-brand-saffron">
            View full details
            <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>

        <ChevronRight className="w-5 h-5 text-ink-300 shrink-0 self-center transition-transform group-hover:translate-x-1 group-hover:text-brand-purple" />
      </div>
    </Link>
  );
}

/* ----------------------------- filter select ---------------------------- */

export function FilterSelect({ icon: Icon, value, onChange, allLabel, options }) {
  return (
    <div className="relative group">
      <Icon
        className={
          "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors " +
          (value ? "text-brand-purple" : "text-ink-300 group-hover:text-ink-500")
        }
      />
      <select
        value={value}
        onChange={onChange}
        className={
          "w-full appearance-none rounded-xl border pl-10 pr-9 py-2.5 text-sm font-semibold cursor-pointer " +
          "transition-all focus:outline-none focus:ring-2 focus:ring-brand-purple/40 " +
          (value
            ? "border-brand-purple/50 bg-brand-purple/5 text-ink-900"
            : "border-gray-300 bg-white text-ink-500 hover:border-gray-400")
        }
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o} value={o} className="text-ink-900">
            {o}
          </option>
        ))}
      </select>
      <ChevronDown
        className={
          "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors " +
          (value ? "text-brand-purple" : "text-ink-400")
        }
      />
    </div>
  );
}

/* --------------------------------- page --------------------------------- */

/** Best-effort city for a drive: explicit `city`, else last part of `venue`. */
const getCity = (d) =>
  (d.city && d.city.trim()) ||
  (d.venue ? d.venue.split(",").pop().trim() : "");

export default function WalkinDrives({ user, isAuthenticated, onLogout }) {
  const navigate = useNavigate();

  const [drives, setDrives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const data = await walkinDriveService.list();
        // Newest backend-managed drives on top, then the legacy hard-coded ones —
        // de-duped by slug (drive.id) so a drive never appears twice.
        const backendOnly = data.filter(
          (d) => !WALKIN_DRIVES.some((h) => h.id === d.id)
        );
        if (active) setError(null);
        if (active) setDrives([...backendOnly, ...WALKIN_DRIVES]);
      } catch (e) {
        // Backend unavailable — still show the hard-coded drives.
        if (active) setError(null);
        if (active) setDrives(WALKIN_DRIVES);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [company, setCompany] = useState("");
  const [experience, setExperience] = useState("");

  const cities = useMemo(
    () => [...new Set(drives.map(getCity).filter(Boolean))].sort(),
    [drives]
  );
  const companies = useMemo(
    () => [...new Set(drives.map((d) => d.company).filter(Boolean))].sort(),
    [drives]
  );
  const experiences = useMemo(
    () =>
      [...new Set(drives.map((d) => d.meta?.experience).filter(Boolean))].sort(),
    [drives]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return drives.filter((d) => {
      if (city && getCity(d) !== city) return false;
      if (company && d.company !== company) return false;
      if (experience && d.meta?.experience !== experience) return false;
      if (q) {
        const hay = [
          d.title,
          d.company,
          d.venue,
          d.meta?.role,
          ...(d.keySkills || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [drives, search, city, company, experience]);

  const hasFilters = Boolean(search || city || company || experience);
  const clearFilters = () => {
    setSearch("");
    setCity("");
    setCompany("");
    setExperience("");
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Navbar
        onSignIn={() => navigate("/login")}
        onPostJob={() =>
          window.open("https://recruiter.jobxbharat.com/", "_blank")
        }
        user={user}
        isAuthenticated={isAuthenticated}
        onLogout={onLogout}
      />

      {/* Hero */}
      <header className="pt-[64px] md:pt-[72px]">
        <div className="bg-gradient-to-br from-brand-purple via-brand-deep to-brand-purple text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-white text-[11px] font-black uppercase tracking-wide">
              <Sparkles className="w-3.5 h-3.5" />
              Now Hiring
            </span>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-black leading-tight">
              Walk-in Drives
            </h1>
            <p className="mt-3 text-sm sm:text-base text-white/80 max-w-2xl leading-relaxed">
              Browse live walk-in interview drives across India. Tap any drive to
              open its full job description, requirements and venue details — then
              just walk in and get hired.
            </p>
            {!loading && !error && (
              <div className="mt-5 inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-white/70">
                <CalendarDays className="w-4 h-4" />
                {drives.length} active {drives.length === 1 ? "drive" : "drives"}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Drives list */}
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Search & filters */}
          {!loading && !error && drives.length > 0 && (
            <div className="mb-6 sm:mb-8 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-300" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by title, company, venue or skill…"
                  className="w-full rounded-xl border border-gray-300 bg-white pl-10 pr-9 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-purple/40 focus:border-brand-purple"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-500"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FilterSelect
                  icon={MapPin}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  allLabel="All cities"
                  options={cities}
                />
                <FilterSelect
                  icon={Building2}
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  allLabel="All companies"
                  options={companies}
                />
                <FilterSelect
                  icon={Clock}
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  allLabel="All experience"
                  options={experiences}
                />
              </div>

              {hasFilters && (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs font-semibold text-ink-500">
                    {filtered.length} of {drives.length} drives
                  </span>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center gap-1 text-xs font-bold text-brand-purple hover:underline"
                  >
                    <X className="w-3.5 h-3.5" /> Clear filters
                  </button>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-purple border-t-transparent" />
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
              <p className="text-sm font-semibold text-red-700">{error}</p>
            </div>
          ) : drives.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center">
              <p className="font-display text-lg font-black text-ink-900">
                No active walk-in drives right now
              </p>
              <p className="mt-2 text-sm text-ink-500">
                Check back soon — new drives are added regularly.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center">
              <p className="font-display text-lg font-black text-ink-900">
                No drives match your filters
              </p>
              <p className="mt-2 text-sm text-ink-500">
                Try a different search, or clear the filters.
              </p>
              <button
                type="button"
                onClick={clearFilters}
                className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-black tracking-wide text-white bg-brand-purple hover:brightness-110 active:scale-95 transition-all"
              >
                <X className="w-4 h-4" /> Clear filters
              </button>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4 sm:space-y-5"
            >
              {filtered.map((drive) => (
                <DriveCard key={drive.id} drive={drive} />
              ))}
            </motion.div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
